class InstagramVideoGrid extends HTMLElement {
  connectedCallback() {
    this.videos = Array.from(this.querySelectorAll('video'));
    this.cards = Array.from(this.querySelectorAll('.instagram-video-card'));

    this.videos.forEach((video) => {
      video.muted = true;
      video.playsInline = true;
    });

    this.setupAutoplay();
    this.setupHover();
    this.setupMobileFocus();
  }

  setupAutoplay() {
    if (!this.videos.length) return;

    if (!('IntersectionObserver' in window)) {
      this.videos.forEach((video) => video.play().catch(() => {}));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    this.videos.forEach((video) => this.observer.observe(video));
  }

  setupHover() {
    this.cards.forEach((card) => {
      card.addEventListener('mouseenter', () => {
        this.classList.add('is-hovering');
        card.classList.add('is-active');
      });
      card.addEventListener('mouseleave', () => {
        this.classList.remove('is-hovering');
        card.classList.remove('is-active');
      });
      card.addEventListener('focusin', () => {
        this.classList.add('is-hovering');
        card.classList.add('is-active');
      });
      card.addEventListener('focusout', () => {
        this.classList.remove('is-hovering');
        card.classList.remove('is-active');
      });
    });
  }

  setupMobileFocus() {
    if (!this.cards.length) return;
    if (!('IntersectionObserver' in window)) return;

    // Mobile has no hover, but the horizontal scroll-snap carousel gives
    // an equivalent gesture: whichever card is most visible as the
    // visitor swipes reuses the exact same "is-hovering"/"is-active"
    // classes as the desktop hover effect, so the next video comes into
    // focus while the previous one blurs out - same CSS, no new rules.
    this.mobileQuery = window.matchMedia('(max-width: 749px)');

    this.focusObserver = new IntersectionObserver(
      (entries) => {
        if (!this.mobileQuery.matches) return;

        entries.forEach((entry) => {
          entry.target.dataset.visibleRatio = entry.intersectionRatio;
        });

        const mostVisible = this.cards.reduce((best, card) => {
          const ratio = parseFloat(card.dataset.visibleRatio) || 0;
          const bestRatio = best ? parseFloat(best.dataset.visibleRatio) || 0 : -1;
          return ratio > bestRatio ? card : best;
        }, null);

        this.cards.forEach((card) => card.classList.toggle('is-active', card === mostVisible));
        this.classList.toggle('is-hovering', Boolean(mostVisible));
      },
      { root: this, threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    this.cards.forEach((card) => this.focusObserver.observe(card));

    this.mobileQuery.addEventListener('change', () => {
      if (this.mobileQuery.matches) return;
      this.classList.remove('is-hovering');
      this.cards.forEach((card) => card.classList.remove('is-active'));
    });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.focusObserver) this.focusObserver.disconnect();
  }
}

customElements.define('instagram-video-grid', InstagramVideoGrid);
